import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export function useAttributesTranslations() {
  const { t } = useTranslation();

  // Memoized complex helpers to prevent re-renders
  const getMainPageText = useMemo(() => {
    const getOptionListText = (key: string, params?: Record<string, any>): string => {
      return String(t(`attributes.optionList.${key}`, params || {}));
    };

    const getAddAttributeModalText = (key: string, params?: Record<string, any>): string => {
      return String(t(`attributes.addAttributeModal.${key}`, params || {}));
    };

    return {
      // Direct translation functions
      title: String(t('attributes.title')),
      description: String(t('attributes.description')),
      loading: String(t('attributes.loading')),
      noAttributes: String(t('attributes.noAttributes')),
      attributesAvailable: (count: number) =>
        String(t('attributes.attributesAvailable', { count })),

      // Nested objects
      table: {
        headers: {
          name: String(t('attributes.table.headers.name')),
          type: String(t('attributes.table.headers.type')),
          code: String(t('attributes.table.headers.code')),
          label: String(t('attributes.table.headers.label')),
          localisable: String(t('attributes.table.headers.localisable')),
          scopable: String(t('attributes.table.headers.scopable')),
          usage: String(t('attributes.table.headers.usage')),
          group: String(t('attributes.table.headers.group')),
          localizable: String(t('attributes.table.headers.localizable')),
          actions: String(t('attributes.table.headers.actions')),
        },
        booleanValues: {
          yes: String(t('attributes.table.booleanValues.yes')),
          no: String(t('attributes.table.booleanValues.no')),
        },
        tooltips: {
          editAttribute: String(t('attributes.table.tooltips.editAttribute')),
          deleteAttribute: String(t('attributes.table.tooltips.deleteAttribute')),
          readOnlyMode: String(t('attributes.table.tooltips.readOnlyMode')),
          coreAttribute: String(t('attributes.table.tooltips.coreAttribute')),
          inUseAttribute: String(t('attributes.table.tooltips.inUseAttribute')),
          localeSupport: String(t('attributes.table.tooltips.localeSupport')),
          channelSupport: String(t('attributes.table.tooltips.channelSupport')),
        },
        badges: {
          required: String(t('attributes.table.badges.required')),
          locale: String(t('attributes.table.badges.locale')),
          channel: String(t('attributes.table.badges.channel')),
          core: String(t('attributes.table.badges.core')),
        },
        configuration: {
          options: String(t('attributes.table.configuration.options')),
          units: String(t('attributes.table.configuration.units')),
          currency: String(t('attributes.table.configuration.currency')),
          types: String(t('attributes.table.configuration.types')),
          validationRule: String(t('attributes.table.configuration.validationRule')),
          noOptions: String(t('attributes.table.configuration.noOptions')),
        },
      },

      actions: {
        addAttribute: String(t('attributes.actions.addAttribute')),
        newAttribute: String(t('attributes.actions.newAttribute')),
        cancel: String(t('attributes.actions.cancel')),
        delete: String(t('attributes.actions.delete')),
        save: String(t('attributes.actions.save')),
        saving: String(t('attributes.actions.saving')),
        retry: String(t('attributes.actions.retry')),
      },

      form: {
        createTitle: String(t('attributes.form.createTitle')),
        editTitle: String(t('attributes.form.editTitle')),
        createDescription: String(t('attributes.form.createDescription')),
        editDescription: String(t('attributes.form.editDescription')),
        fields: {
          name: String(t('attributes.form.fields.name')),
          nameRequired: String(t('attributes.form.fields.nameRequired')),
          namePlaceholder: String(t('attributes.form.fields.namePlaceholder')),
          code: String(t('attributes.form.fields.code')),
          codeRequired: String(t('attributes.form.fields.codeRequired')),
          codePlaceholder: String(t('attributes.form.fields.codePlaceholder')),
          dataType: String(t('attributes.form.fields.dataType')),
          dataTypeRequired: String(t('attributes.form.fields.dataTypeRequired')),
          group: String(t('attributes.form.fields.group')),
          groupOptional: String(t('attributes.form.fields.groupOptional')),
          localizable: String(t('attributes.form.fields.localizable')),
          localizableDescription: String(t('attributes.form.fields.localizableDescription')),
          scopable: String(t('attributes.form.fields.scopable')),
          scopableDescription: String(t('attributes.form.fields.scopableDescription')),
          unique: String(t('attributes.form.fields.unique')),
          uniqueDescription: String(t('attributes.form.fields.uniqueDescription')),
          required: String(t('attributes.form.fields.required')),
          requiredDescription: String(t('attributes.form.fields.requiredDescription')),
          defaultValue: String(t('attributes.form.fields.defaultValue')),
          defaultValuePlaceholder: String(t('attributes.form.fields.defaultValuePlaceholder')),
          validationRules: String(t('attributes.form.fields.validationRules')),
          validationRulesDescription: String(
            t('attributes.form.fields.validationRulesDescription')
          ),
          minLength: String(t('attributes.form.fields.minLength')),
          maxLength: String(t('attributes.form.fields.maxLength')),
          minValue: String(t('attributes.form.fields.minValue')),
          maxValue: String(t('attributes.form.fields.maxValue')),
          pattern: String(t('attributes.form.fields.pattern')),
          patternDescription: String(t('attributes.form.fields.patternDescription')),
          decimals: String(t('attributes.form.fields.decimals')),
          decimalsDescription: String(t('attributes.form.fields.decimalsDescription')),
          dateMin: String(t('attributes.form.fields.dateMin')),
          dateMax: String(t('attributes.form.fields.dateMax')),
          options: String(t('attributes.form.fields.options')),
          optionsDescription: String(t('attributes.form.fields.optionsDescription')),
          multipleValues: String(t('attributes.form.fields.multipleValues')),
          multipleValuesDescription: String(t('attributes.form.fields.multipleValuesDescription')),
          wysiwyg: String(t('attributes.form.fields.wysiwyg')),
          wysiwygDescription: String(t('attributes.form.fields.wysiwygDescription')),
        },
      },

      dataTypes: {
        text: String(t('attributes.dataTypes.text')),
        textarea: String(t('attributes.dataTypes.textarea')),
        number: String(t('attributes.dataTypes.number')),
        boolean: String(t('attributes.dataTypes.boolean')),
        date: String(t('attributes.dataTypes.date')),
        option_simple: String(t('attributes.dataTypes.option_simple')),
        option_multi: String(t('attributes.dataTypes.option_multi')),
        measurement: String(t('attributes.dataTypes.measurement')),
        price_collection: String(t('attributes.dataTypes.price_collection')),
        image: String(t('attributes.dataTypes.image')),
        file: String(t('attributes.dataTypes.file')),
      },

      dialogs: {
        deleteTitle: String(t('attributes.dialogs.deleteTitle')),
        deleteDescription: String(t('attributes.dialogs.deleteDescription')),
        deleteCancel: String(t('attributes.dialogs.deleteCancel')),
        deleteConfirm: String(t('attributes.dialogs.deleteConfirm')),
      },

      messages: {
        createSuccess: String(t('attributes.messages.createSuccess')),
        updateSuccess: String(t('attributes.messages.updateSuccess')),
        deleteSuccess: String(t('attributes.messages.deleteSuccess')),
        createError: String(t('attributes.messages.createError')),
        updateError: String(t('attributes.messages.updateError')),
        deleteError: String(t('attributes.messages.deleteError')),
        loadError: String(t('attributes.messages.loadError')),
      },

      validation: {
        codeRequired: String(t('attributes.validation.codeRequired')),
        codeInvalid: String(t('attributes.validation.codeInvalid')),
        codeExists: String(t('attributes.validation.codeExists')),
        labelRequired: String(t('attributes.validation.labelRequired')),
        dataTypeRequired: String(t('attributes.validation.dataTypeRequired')),
      },

      optionList: {
        add: getOptionListText('add'),
        addFirstOption: getOptionListText('addFirstOption'),
        newOption: getOptionListText('newOption'),
        placeholder: getOptionListText('placeholder'),
        remove: getOptionListText('remove'),
        confirmRemove: getOptionListText('confirmRemove'),
        noOptions: getOptionListText('noOptions'),
        dragToReorder: getOptionListText('dragToReorder'),
      },

      addAttributeModal: {
        title: getAddAttributeModalText('title'),
        description: getAddAttributeModalText('description'),
        standardView: getAddAttributeModalText('standardView'),
        advancedOptions: getAddAttributeModalText('advancedOptions'),
        searchPlaceholder: getAddAttributeModalText('searchPlaceholder'),
        filterLabel: getAddAttributeModalText('filterLabel'),
        filterAll: getAddAttributeModalText('filterAll'),
        noResults: getAddAttributeModalText('noResults'),
        noResultsDescription: getAddAttributeModalText('noResultsDescription'),
        selectAttribute: getAddAttributeModalText('selectAttribute'),
        attributeDetailsSectionTitle: getAddAttributeModalText('attributeDetailsSectionTitle'),
        attributeDetailsSectionDesc: getAddAttributeModalText('attributeDetailsSectionDesc'),
        localeLabel: getAddAttributeModalText('localeLabel'),
        selectLocale: getAddAttributeModalText('selectLocale'),
        allLocales: getAddAttributeModalText('allLocales'),
        channelLabel: getAddAttributeModalText('channelLabel'),
        selectChannel: getAddAttributeModalText('selectChannel'),
        allChannels: getAddAttributeModalText('allChannels'),
        typeLabels: {
          text: getAddAttributeModalText('typeLabels.text'),
          number: getAddAttributeModalText('typeLabels.number'),
          boolean: getAddAttributeModalText('typeLabels.boolean'),
          date: getAddAttributeModalText('typeLabels.date'),
          option_simple: getAddAttributeModalText('typeLabels.option_simple'),
          option_multi: getAddAttributeModalText('typeLabels.option_multi'),
        },
        localizableLabel: getAddAttributeModalText('localizableLabel'),
        scopableLabel: getAddAttributeModalText('scopableLabel'),
        addButton: getAddAttributeModalText('addButton'),
        cancelButton: getAddAttributeModalText('cancelButton'),
        addingButton: getAddAttributeModalText('addingButton'),
        groupedBy: {
          title: getAddAttributeModalText('groupedBy.title'),
          noAttributes: (type: string) =>
            getAddAttributeModalText('groupedBy.noAttributes', { type }),
        },
      },

      enhanced: {
        modal: {
          buttons: {
            cancel: String(t('attributes.enhanced.modal.buttons.cancel')),
            saving: String(t('attributes.enhanced.modal.buttons.saving')),
            createAttribute: String(t('attributes.enhanced.modal.buttons.createAttribute')),
            updateAttribute: String(t('attributes.enhanced.modal.buttons.updateAttribute')),
          },
        },
      },
    };
  }, [t]);

  return getMainPageText;
}
